import axios from 'axios'
import { handleTokenExpiration } from '../utils/authHelpers'
import {
  clearClientSession,
  getLegacyReadableToken,
  getMemoryToken,
  getPersistedToken,
  hasSessionHint,
} from '../utils/authSession'

// En desarrollo, usar ruta relativa para aprovechar el proxy de Vite
// En producción, usar la variable de entorno VITE_API_URL
const API_BASE_URL = import.meta.env.DEV
  ? '/api'
  : (import.meta.env.VITE_API_URL || 'http://localhost:8000/api')

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

api.interceptors.request.use(
  (config) => {
    const token = getMemoryToken() || getPersistedToken() || getLegacyReadableToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else if (import.meta.env.DEV && !hasSessionHint()) {
      // Sin sesión: muchos endpoints públicos no requieren token.
      // Evitamos ruido salvo depuración explícita.
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
      if (import.meta.env.DEV) {
        const formDataEntries = Array.from(config.data.entries())
        const files = formDataEntries.filter(([, value]) => value instanceof File)
        console.log('📤 Interceptor: Enviando FormData:', {
          url: config.url,
          method: config.method,
          baseURL: config.baseURL,
          fullURL: `${config.baseURL}${config.url}`,
          totalEntries: formDataEntries.length,
          hasFiles: files.length > 0,
          files: files.map(([key, value]) => ({
            field: key,
            name: value.name,
            size: value.size,
            type: value.type,
          })),
          textFields: formDataEntries
            .filter(([, value]) => !(value instanceof File))
            .map(([key, value]) => ({ field: key, value })),
        })
      }
    }

    return config
  },
  (error) => Promise.reject(error)
)

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const currentPath = window.location.pathname
    const isAuthRoute = currentPath === '/login' || currentPath === '/register' || currentPath.startsWith('/auth/')
    const errorUrl = error.config?.url || ''
    const isAuthEndpoint =
      errorUrl.includes('/auth/login') ||
      errorUrl.includes('/auth/register') ||
      errorUrl.includes('/auth/login-phone') ||
      errorUrl.includes('/auth/logout')

    if (error.response?.status === 429) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Demasiados intentos. Por favor, intente más tarde.'
      console.warn('⚠️ Error 429 - Demasiados intentos:', {
        url: errorUrl,
        message: errorMessage,
      })
      return Promise.reject(error)
    }

    if (error.response?.status === 403) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'No tienes permisos para esta acción'
      if (import.meta.env.DEV) {
        console.warn('⚠️ Error 403 - Sin permisos:', { url: errorUrl, message })
      }
      try {
        window.dispatchEvent(new CustomEvent('api:forbidden', { detail: { message, url: errorUrl } }))
      } catch {
        // ignore
      }
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Token inválido o expirado'

      if (isAuthEndpoint || isAuthRoute) {
        console.warn('⚠️ Error 401 en autenticación:', {
          url: errorUrl,
          message: errorMessage,
        })
        return Promise.reject(error)
      }

      const hadSession = hasSessionHint()
      if (hadSession) {
        console.error('❌ Error 401 - Token inválido o expirado:', {
          url: errorUrl,
          message: errorMessage,
          currentPath,
        })

        clearClientSession()

        try {
          handleTokenExpiration()
        } catch (err) {
          console.warn('⚠️ No se pudo llamar al logout handler, redirigiendo manualmente:', err)
        }

        if (!isAuthRoute) {
          window.location.href = '/login'
        }
      } else {
        console.warn('⚠️ Error 401 sin sesión activa:', {
          url: errorUrl,
          message: errorMessage,
        })
      }
    }

    return Promise.reject(error)
  }
)

export default api
